import * as React from "react";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { Checkbox } from "@progress/kendo-react-inputs";
import { useEffect, useState, useMemo } from "react";
import { AmountFormatCell } from "components/common/formatter";
import { NumericCell } from "./DisbursementInputField";
import { DateFormatCell } from "components/common/formatter";
import { process } from "@progress/kendo-data-query";
const initialDataState = {
  sort: [{ field: "date", dir: "asc" }],
  take: 50,
  skip: 0,
};
const DisbursementGrid = (props) => {
  const [dataState, setDataState] = useState(initialDataState);
  const [data, setData] = useState([]);

  useEffect(() => {
    if (props.data) setData(props.data);
  }, [props.data]);

  const dataStateChange = (e) => {
    if (
      e.dataState.sort !== undefined &&
      e.dataState.skip !== 0 &&
      dataState.sort !== e.dataState.sort
    ) {
      setDataState({ ...e.dataState, skip: 0 });
    } else if (e.dataState.sort[0] === undefined) {
      setDataState({
        ...e.dataState,
        sort: [
          {
            field: dataState.sort[0].field,
            dir: "asc",
          },
        ],
      });
    } else {
      setDataState(e.dataState);
    }
  };

  const mergeAllocation = () => {
    const result = [];
    data.map((item) => {
      if (item.add === true) {
        const data = {
          postingId: item.postingId,
          amount: item.amount,
          details: item.details,
        };
        result.push(data);
      }
    });
    props.updateAllocations(result);
  };

  const AmountNameHeader = (props) => {
    return (
      <div
        onClick={(e) => {
          props.onClick(e);
        }}
        style={{ textAlign: "center", cursor: "pointer" }}>
        {props.title}
        {props.children}
      </div>
    );
  };

  const handleSelectAll = () => {
    setData((prevData) => {
      if (!prevData.length) return prevData;

      const allSelected = prevData.every((row) => row.add === true);
      if (allSelected) {
        return prevData.map((item) => ({ ...item, add: false, amount: 0.0 }));
      }

      return prevData.map((item) => ({
        ...item,
        add: true,
        amount: item.amountDue,
      }));
    });
  };

  const SelectAllHeader = (props) => {
    const some = data.some((r) => r.add);
    const all = data.length > 0 && data.every((r) => r.add);
    const checked = data.length === 0 ? false : all ? true : some ? null : false;

    return (
      <div className="d-flex align-items-center justify-content-center text-center gap-1">
        <div
          onClick={(e) => props.onClick(e)}
          className="k-link"
          style={{ cursor: "pointer" }}>
          {props.title}
          {props.children}
        </div>
        <Checkbox
          checked={checked}
          className="ms-2"
          aria-label="Select all rows"
          onChange={handleSelectAll}
        />
      </div>
    );
  };

  const totalAllocatingAmount = useMemo(() => {
    return data.reduce((total, item) => {
      if (item.add === true && item.amount !== null && item.amount !== undefined) {
        return total + parseFloat(item.amount || 0);
      }
      return total;
    }, 0);
  }, [data]);

  useEffect(() => {
    mergeAllocation();
  }, [data]);

  const itemChange = (e) => {
    const targetAmount = props.amountRef?.current ?? props.Amount ?? 0;
    const allocateForRow = (remaining, amountDue) => {
      if (amountDue >= 0) {
        if (remaining <= 0) return 0;
        return Math.min(remaining, amountDue);
      }
      const allocatedAmount = Math.max(remaining, amountDue);
      return allocatedAmount < 0 ? allocatedAmount : amountDue;
    };

    setData((prevData) =>
      prevData.map((item) => {
        if (item.transactionNo !== e.dataItem.transactionNo) return item;

        const updatedItem = { ...item };

        if (e.field === "amount") {
          const isValidPositive = e.value <= item.amountDue && item.amountDue >= 0;
          const isValidNegative =
            e.value < 0 && e.value > item.amountDue && item.amountDue < 0;

          if (isValidPositive || isValidNegative) {
            updatedItem.amount = e.value;
            if (e.value !== 0 && e.value !== null && !updatedItem.add) {
              updatedItem.add = true;
            }
            if ((e.value === 0 || e.value === null) && updatedItem.add) {
              updatedItem.add = false;
            }
          }
        } else if (e.field === "add") {
          updatedItem.add = e.value;
          if (e.value) {
            if (!targetAmount) {
              updatedItem.amount = item.amountDue;
              return updatedItem;
            }
            const selectedSumExcludingThis = prevData.reduce((sum, row) => {
              if (!row.add) return sum;
              if (row.transactionNo === item.transactionNo) return sum;
              return sum + (row.amount || 0);
            }, 0);
            const remaining = targetAmount - selectedSumExcludingThis;
            updatedItem.amount = allocateForRow(remaining, item.amountDue);
          } else {
            updatedItem.amount = 0.0;
          }
        }

        return updatedItem;
      }),
    );
  };

  return (
    <Grid
      data={process(data, dataState)}
      dataItemKey={"transactionNo"}
      onItemChange={itemChange}
      editField="inEdit"
      onDataStateChange={dataStateChange}
      pageable={true}
      {...dataState}
      sortable={true}
      className="flex-grow-1 overflow-auto grid-minwidth-col3-150">
      <Column
        field="date"
        title="Date"
        editable={false}
        cell={DateFormatCell}
        headerClassName="ps-4"
        width="100px"
      />
      <Column field="reference" title="Reference" width="100px" editable={false} />
      <Column field="details" title="Details" editable={false} />
      <Column
        field="vatAmount"
        title="VAT"
        editable={false}
        cell={AmountFormatCell}
        headerCell={AmountNameHeader}
        width="100px"
      />
      <Column
        field="grossAmount"
        title="Gross Amount"
        editable={false}
        cell={AmountFormatCell}
        headerCell={AmountNameHeader}
        width="120px"
      />
      <Column
        field="amountPaid"
        title="Paid"
        editable={false}
        cell={AmountFormatCell}
        headerCell={AmountNameHeader}
        width="120px"
      />
      <Column
        field="amountDue"
        title="Due"
        editable={false}
        cell={AmountFormatCell}
        headerCell={AmountNameHeader}
        width="120px"
      />
      <Column
        field="add"
        title="Select"
        editor="boolean"
        width="80px"
        className="text-center"
        headerClassName="text-center"
        sortable={false}
        headerCell={SelectAllHeader}
      />
      <Column
        field="amount"
        title="Allocate"
        editor="numeric"
        width={"150px"}
        cell={NumericCell}
        headerCell={AmountNameHeader}
        footerCell={() => (
          <td
            style={{
              textAlign: "center",
              fontWeight: "bold",
            }}>
            Total: {totalAllocatingAmount.toFixed(2)}
          </td>
        )}
      />
    </Grid>
  );
};

export default React.memo(DisbursementGrid);
